// src/components/website-preview/ServiceDetailPage.js
import React from 'react';
import { Clock, Video, MapPin, ArrowLeft } from 'lucide-react';
import { sanitizeHtml } from '../../utils/sanitize';

const ServiceDetailPage = ({
  service,
  allServices = [],
  designSettings,
  practitionerName,
  logo,
  onBack,
  onBookService,
  isPreview = false,
}) => {
  // Design settings
  const colors = designSettings?.colors || { primary: '#7c3aed', text: '#1F2937' };
  const fonts = designSettings?.fonts || { heading: 'Montserrat', body: 'Montserrat' };

  // Button settings from design context
  const buttonStyle = designSettings?.buttonStyle || { cornerRadius: 'rounded', size: 'medium' };

  // Calculate border radius from buttonStyle.cornerRadius
  const borderRadius = buttonStyle.cornerRadius === 'sharp' ? '0px'
    : buttonStyle.cornerRadius === 'very-rounded' ? '9999px'
    : '8px'; // rounded default

  // Calculate padding from buttonStyle.size
  const buttonPadding = buttonStyle.size === 'compact' ? '6px 16px'
    : buttonStyle.size === 'large' ? '14px 32px'
    : '10px 24px'; // medium default

  const buttonFontSize = buttonStyle.size === 'compact' ? '13px'
    : buttonStyle.size === 'large' ? '16px'
    : '14px'; // medium default

  // Get session type display
  const getSessionTypeDisplay = () => {
    if (service.serviceType === 'in-person') return 'In-Person';
    if (service.serviceType === 'phone') return 'Phone';
    if (service.serviceType === 'hybrid') return 'Hybrid';
    return 'Virtual';
  };

  // Get other services (exclude current one)
  const otherServices = allServices
    .filter(s => s.active && s.id !== service.id)
    .slice(0, 3);

  // Check if content exists and is not empty
  const hasContent = (html) => {
    return html && html !== '<p></p>' && html.trim() !== '';
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Navigation Bar */}
      <nav
        style={{
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #E5E7EB',
          background: '#FFFFFF',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {logo}
        </div>
        <button
          onClick={onBookService}
          style={{
            padding: buttonPadding,
            background: 'transparent',
            color: colors.primary,
            border: `2px solid ${colors.primary}`,
            borderRadius: borderRadius,
            fontSize: buttonFontSize,
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: fonts.body,
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = colors.primary;
            e.currentTarget.style.color = '#FFFFFF';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = colors.primary;
          }}
        >
          Book a Session
        </button>
      </nav>

      {/* Back link */}
      {/* TODO: On published website, this should navigate to /#services anchor, not call onBack() */}
      <div style={{ padding: '12px 24px', background: '#FFFFFF' }}>
        <button
          onClick={onBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '13px',
            color: '#6B7280',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            fontFamily: fonts.body,
            transition: 'color 0.2s ease',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = colors.primary)}
          onMouseLeave={(e) => (e.currentTarget.style.color = '#6B7280')}
        >
          <ArrowLeft size={14} />
          Back to Services
        </button>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, background: '#FFFFFF' }}>
        {/* Service Hero Image */}
        {service.serviceImage && (
          <div
            style={{
              width: '100%',
              maxHeight: '360px',
              overflow: 'hidden',
              marginBottom: '32px',
            }}
          >
            <img
              src={service.serviceImage}
              alt={service.name}
              style={{
                width: '100%',
                height: '360px',
                objectFit: 'cover',
                objectPosition: 'center',
              }}
            />
          </div>
        )}

        {/* Content Container */}
        <div
          style={{
            maxWidth: '720px',
            margin: '0 auto',
            padding: '0 24px 48px',
          }}
        >
          {/* Service Header */}
          <h1
            style={{
              fontFamily: fonts.heading,
              fontSize: '32px',
              fontWeight: 700,
              color: colors.text,
              marginBottom: '8px',
            }}
          >
            {service.name}
          </h1>

          {/* Tagline */}
          {service.tagline && (
            <p
              style={{
                fontSize: '18px',
                color: '#6B7280',
                lineHeight: '1.5',
                marginBottom: '24px',
                fontFamily: fonts.body,
              }}
            >
              {service.tagline}
            </p>
          )}

          {/* Quick Facts Bar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '24px',
              padding: '16px 20px',
              background: '#F9FAFB',
              borderRadius: '10px',
              marginBottom: '32px',
              flexWrap: 'wrap',
              fontFamily: fonts.body,
            }}
          >
            {/* Duration */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Clock size={16} color="#6B7280" />
              <span style={{ fontSize: '14px', color: '#4B5563' }}>
                {service.duration} minutes
              </span>
            </div>

            {/* Divider */}
            <div style={{ width: '1px', height: '20px', background: '#E5E7EB' }} />

            {/* Session type */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {service.serviceType === 'virtual' || service.serviceType === 'hybrid' ? (
                <Video size={16} color="#6B7280" />
              ) : (
                <MapPin size={16} color="#6B7280" />
              )}
              <span
                style={{
                  fontSize: '14px',
                  color: '#4B5563',
                  textTransform: 'capitalize',
                }}
              >
                {getSessionTypeDisplay()}
              </span>
            </div>

            {/* Divider */}
            <div style={{ width: '1px', height: '20px', background: '#E5E7EB' }} />

            {/* Price */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '18px', fontWeight: 700, color: colors.text }}>
                ${service.price}
              </span>
            </div>
          </div>

          {/* Full Description */}
          {hasContent(service.description) && (
            <div style={{ marginBottom: '40px' }}>
              <div
                className="service-detail-content"
                style={{
                  fontSize: '15px',
                  lineHeight: '1.8',
                  color: '#374151',
                  fontFamily: fonts.body,
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(service.description) }}
              />
            </div>
          )}

          {/* What to Expect Section */}
          {hasContent(service.whatToExpect) && (
            <div style={{ marginBottom: '40px' }}>
              <h2
                style={{
                  fontFamily: fonts.heading,
                  fontSize: '22px',
                  fontWeight: 600,
                  color: colors.text,
                  marginBottom: '16px',
                }}
              >
                What to Expect
              </h2>
              <div
                className="service-detail-content"
                style={{
                  fontSize: '15px',
                  lineHeight: '1.8',
                  color: '#374151',
                  fontFamily: fonts.body,
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(service.whatToExpect) }}
              />
            </div>
          )}

          {/* Who Is This For Section */}
          {hasContent(service.whoIsThisFor) && (
            <div style={{ marginBottom: '40px' }}>
              <h2
                style={{
                  fontFamily: fonts.heading,
                  fontSize: '22px',
                  fontWeight: 600,
                  color: colors.text,
                  marginBottom: '16px',
                }}
              >
                Who Is This For
              </h2>
              <div
                className="service-detail-content"
                style={{
                  fontSize: '15px',
                  lineHeight: '1.8',
                  color: '#374151',
                  fontFamily: fonts.body,
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(service.whoIsThisFor) }}
              />
            </div>
          )}

          {/* Book This Service CTA */}
          <div
            style={{
              textAlign: 'center',
              padding: '40px 24px',
              margin: '0 -24px',
              background: '#F9FAFB',
              borderRadius: '12px',
              marginBottom: otherServices.length > 0 ? '48px' : '0',
            }}
          >
            <h3
              style={{
                fontFamily: fonts.heading,
                fontSize: '20px',
                fontWeight: 600,
                color: colors.text,
                marginBottom: '8px',
              }}
            >
              Ready to book?
            </h3>
            <p
              style={{
                fontSize: '14px',
                color: '#6B7280',
                marginBottom: '20px',
                fontFamily: fonts.body,
              }}
            >
              {service.duration} minutes · ${service.price}
            </p>
            <button
              onClick={onBookService}
              style={{
                padding: buttonPadding,
                background: colors.primary,
                color: '#FFFFFF',
                border: 'none',
                borderRadius: borderRadius,
                fontSize: buttonFontSize,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontFamily: fonts.body,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Book This Service
            </button>
          </div>

          {/* Other Services Section */}
          {otherServices.length > 0 && (
            <div style={{ marginBottom: '48px' }}>
              <h2
                style={{
                  fontFamily: fonts.heading,
                  fontSize: '20px',
                  fontWeight: 600,
                  color: colors.text,
                  marginBottom: '20px',
                  textAlign: 'center',
                }}
              >
                Explore Other Services
              </h2>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                  gap: '16px',
                }}
              >
                {otherServices.map((other) => (
                  <MiniServiceCard
                    key={other.id}
                    service={other}
                    primaryColor={colors.primary}
                    textColor={colors.text}
                    headingFont={fonts.heading}
                    bodyFont={fonts.body}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer would go here - reuse existing footer component */}
    </div>
  );
};

// Mini Service Card for "Other Services" section
const MiniServiceCard = ({ service, primaryColor, textColor, headingFont, bodyFont }) => {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <div
      style={{
        border: `1px solid ${isHovered ? primaryColor : '#E5E7EB'}`,
        borderRadius: '8px',
        padding: '16px',
        background: '#FFFFFF',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: isHovered ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <h3
        style={{
          fontFamily: headingFont,
          fontSize: '15px',
          fontWeight: 600,
          color: textColor,
          marginBottom: '8px',
        }}
      >
        {service.name}
      </h3>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '12px',
          color: '#9CA3AF',
          marginBottom: '8px',
          fontFamily: bodyFont,
        }}
      >
        <span>{service.duration} min</span>
        <span>·</span>
        <span style={{ fontWeight: 600, color: textColor }}>${service.price}</span>
      </div>
      <div
        style={{
          fontSize: '12px',
          fontWeight: 500,
          color: primaryColor,
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          fontFamily: bodyFont,
        }}
      >
        View
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
};

export default ServiceDetailPage;
